/**
 * MÀN CHƠI · ĐỘ KHÓ · BẢNG THƯỞNG QUA MÀN — THUẦN DỮ LIỆU + QUY LUẬT.
 *
 * Quy tắc kiến trúc (README): `data/` là SỐ LIỆU, `sim/` là LOGIC THUẦN.
 * File này KHÔNG import Phaser và KHÔNG đụng tới DOM ⇒ chạy được trong test, trong
 * `tools/balance.ts`, và trong cả UI lẫn sim. Mọi con số cân bằng của màn/ độ khó/ thưởng
 * nằm ở ĐÂY, sửa không cần build lại code.
 *
 * ⚠️ BẪY ĐÃ GẶP (ghi lại):
 * 1) Luật mở khoá từng bị chép làm 3 bản (UI khoá nút, save migrate, sim kiểm tra) ⇒ sửa 1 chỗ
 *    quên 2 chỗ kia, sinh ra cảnh "nút mở nhưng vào trận bị đá ra". Nay luật nằm DUY NHẤT trong
 *    `isUnlocked()`; UI/ sim/ save đều gọi lại hàm này.
 * 2) `cleared` là mảng CHUỖI KHOÁ `'1.1:easy'` chứ không phải mảng object: save cũ đọc lên vẫn
 *    hiểu, và so sánh `includes()` rẻ hơn dựng Map mỗi khung hình.
 * 3) Mọi hàm công khai phải CHỊU ĐƯỢC DỮ LIỆU RÁC (save hỏng, id lạ, `undefined`). Trả
 *    `undefined`/`null`/`false`/`[]` — TUYỆT ĐỐI không ném lỗi, vì một save rác không được
 *    phép làm trắng màn hình người chơi.
 */

export type Difficulty = 'easy' | 'normal' | 'elite';

export interface MapDef {
  /** khoá màn, dạng 'chương.màn' — ví dụ '1.1', '3.5' */
  id: string;
  chapter: number;
  index: number;
  name: string;
  /**
   * Chỉ số màn truyền thẳng cho `buildWaves(stageIndex)`.
   * Công thức: `(chapter - 1) * 5 + (index - 1)` ⇒ SỐ NGUYÊN tăng dần 0..14.
   * Màn 1.5 khó hơn 1.1, màn 2.1 khó hơn 1.5 (không nhảy lùi).
   * ⚠️ `buildWaves` nhân thêm `(1 + 0.3 * stageIndex)` máu quái và `(1 + 0.14 * stageIndex)`
   * sát thương: màn 3.5 (stageIndex 14) ⇒ quái ~5,2× máu. Đây là độ dốc CỐ Ý của 15 màn
   * (tương đương 1,5 vòng "stage" cũ), nhưng nếu thấy sốc thì hạ hệ số trong `waves.ts`.
   */
  stageIndex: number;
  blurb: string;
}

export interface DifficultyDef {
  id: Difficulty;
  name: string;
  blurb: string;
  hpMult: number;
  atkMult: number;
  goldMult: number;
  rewardMult: number;
}

/** 5 màn mỗi chương — dùng cho công thức `stageIndex`, đổi số này là dải stage đổi theo. */
export const MAPS_PER_CHAPTER = 5;

/**
 * ĐỘ KHÓ — ĐÚNG THỨ TỰ easy → normal → elite (UI dựa vào thứ tự để vẽ nút và khoá nút).
 *
 * Vì sao elite nhân `rewardMult` tới 1,8 mà vẫn không phá kinh tế: phần kim cương đã được
 * "giảm nhẹ" hệ số (xem `rewardFor`), còn 1,8 chỉ tác động đầy đủ lên TỈ LỆ ĐỒ HIẾM.
 * Người chơi elite bỏ nhiều thời gian hơn ⇒ đáng được nhận nhiều hơn, không phải nhận gấp đôi.
 */
export const DIFFICULTIES: DifficultyDef[] = [
  { id: 'easy', name: 'Dễ', blurb: 'Quái nhẹ, thưởng thấp — để làm quen và mở khoá.', hpMult: 0.85, atkMult: 0.85, goldMult: 0.9, rewardMult: 0.8 },
  { id: 'normal', name: 'Thường', blurb: 'Đúng cân bằng gốc của game.', hpMult: 1, atkMult: 1, goldMult: 1, rewardMult: 1 },
  { id: 'elite', name: 'Elite', blurb: 'Quái trâu và đau hơn hẳn — bù lại tỉ lệ đồ hiếm gấp đôi.', hpMult: 1.35, atkMult: 1.25, goldMult: 1.35, rewardMult: 1.8 },
];

/**
 * Kế hoạch từng chương. Tách `id`/`chapter`/`index`/`stageIndex` khỏi bảng này để chúng KHÔNG
 * BAO GIỜ lệch nhau: id luôn suy ra từ vị trí, không gõ tay 15 lần (gõ tay là có ngày '2.1'
 * nằm sau '2.2' trong mảng ⇒ `nextMap` đi lùi).
 */
const CHAPTER_PLAN: Array<{ chapter: number; maps: Array<{ name: string; blurb: string }> }> = [
  {
    chapter: 1,
    maps: [
      { name: 'Ngõ Chợ Võ Lâm', blurb: 'Ba con đầu gấu chặn ngõ đòi tiền mãi lộ. Xin để lại dép.' },
      { name: 'Sân Đình Làng', blurb: 'Cả xóm kéo ra xem, không ai chịu về, mà cũng không ai đánh giúp.' },
      { name: 'Bãi Rau Sau Hè', blurb: 'Mụ buôn bán "rau" — hoá ra toàn gậy và dép.' },
      { name: 'Cầu Đá Đầu Làng', blurb: 'Lính súng đứng gác. Không có giấy tờ thì đừng mơ qua.' },
      { name: 'Cổng Làng Võ', blurb: 'Trùm xóm xuất hiện, tay cầm… một chiếc dép thần.' },
    ],
  },
  {
    chapter: 2,
    maps: [
      { name: 'Chợ Huyện Vỡ', blurb: 'Hai phe giành sạp. Nhà ngươi đứng giữa, thật là khéo chọn chỗ.' },
      { name: 'Quán Nước Vỉa Hè', blurb: 'Ngồi uống trà đá cũng bị quái hỏi thăm.' },
      { name: 'Xưởng Đúc Vũ Khí', blurb: 'Quái ở đây có giáp thật, đánh mãi mới vào.' },
      { name: 'Đường Đèo Gió', blurb: 'Gió thổi bay cả chảo lửa — sát thương tầm xa dày đặc.' },
      { name: 'Nha Môn Huyện', blurb: 'Quan huyện bán cửa quan cho ma giáo. Đập luôn.' },
    ],
  },
  {
    chapter: 3,
    maps: [
      { name: 'Cổng Tổng Đàn', blurb: 'Cổng son đỏ, hai bên treo… dép của người thua trận.' },
      { name: 'Võ Đài Đêm', blurb: 'Đèn lồng, trống trận, và một đám đông cá cược rất mất dạy.' },
      { name: 'Hầm Rượu Tổng Đàn', blurb: 'Quái say rượu nên đi loạng choạng mà đấm rất đau.' },
      { name: 'Đỉnh Núi Tuyết', blurb: 'Lạnh tới mức chiêu nào cũng chậm hơn nửa nhịp.' },
      { name: 'Tổng Đàn Ma Giáo', blurb: 'Trùm cuối: hắn tự nhận mình là "chính đạo". Kệ hắn.' },
    ],
  },
];

/**
 * 15 MÀN = 3 chương × 5 màn: '1.1'..'1.5', '2.1'..'2.5', '3.1'..'3.5'.
 * Thứ tự mảng CHÍNH LÀ thứ tự chơi ⇒ `prevMap`/`nextMap` chỉ cần dịch 1 ô.
 */
export const MAPS: MapDef[] = CHAPTER_PLAN.flatMap((ch) =>
  ch.maps.map((m, i) => {
    const index = i + 1;
    return {
      id: `${ch.chapter}.${index}`,
      chapter: ch.chapter,
      index,
      name: m.name,
      stageIndex: (ch.chapter - 1) * MAPS_PER_CHAPTER + (index - 1),
      blurb: m.blurb,
    };
  }),
);

/** Màn đầu tiên — LUÔN mở ở easy (điểm vào bắt buộc, không có gì để khoá). */
export const FIRST_MAP_ID = '1.1';

/** Tra màn theo id. Id lạ ⇒ `undefined` (KHÔNG ném lỗi). */
export function mapById(id: string): MapDef | undefined {
  if (typeof id !== 'string') return undefined;
  return MAPS.find((m) => m.id === id);
}

/**
 * Tra độ khó. ⚠️ Chữ ký theo đặc tả trả `DifficultyDef` (không phải `| undefined`) nên khi id lạ
 * ta LÙI VỀ `normal` thay vì ném — UI/ sim gọi hàm này mỗi khung hình, không được phép vỡ.
 */
export function difficultyById(id: Difficulty): DifficultyDef {
  return DIFFICULTIES.find((d) => d.id === id) ?? DIFFICULTIES[1];
}

const DIFFICULTY_IDS: readonly Difficulty[] = DIFFICULTIES.map((d) => d.id);

/** Khoá tiến trình của 1 ô (màn × độ khó): `'1.1:easy'`. */
export function mapKey(mapId: string, diff: Difficulty): string {
  return `${mapId}:${diff}`;
}

/**
 * Giải khoá tiến trình. Trả `null` nếu rác: không đủ 2 phần, độ khó lạ, màn không tồn tại.
 * Vì sao kiểm tra cả màn: save cũ có thể còn khoá của màn đã bị xoá khỏi `MAPS` ⇒ bỏ qua
 * thay vì để nó "chiếm" một ô trong `completionPct`.
 */
export function parseKey(key: string): { mapId: string; diff: Difficulty } | null {
  if (typeof key !== 'string') return null;
  const parts = key.split(':');
  if (parts.length !== 2) return null;
  const [mapId, rawDiff] = parts;
  if (!DIFFICULTY_IDS.includes(rawDiff as Difficulty)) return null;
  if (!mapById(mapId)) return null;
  return { mapId, diff: rawDiff as Difficulty };
}

/** Màn liền trước theo thứ tự chơi (1.2 → 1.1; 2.1 → 1.5). Màn đầu ⇒ `undefined`. */
export function prevMap(mapId: string): MapDef | undefined {
  const i = MAPS.findIndex((m) => m.id === mapId);
  return i > 0 ? MAPS[i - 1] : undefined;
}

/** Màn liền sau theo thứ tự chơi (1.5 → 2.1). Màn cuối ⇒ `undefined`. */
export function nextMap(mapId: string): MapDef | undefined {
  const i = MAPS.findIndex((m) => m.id === mapId);
  return i >= 0 && i < MAPS.length - 1 ? MAPS[i + 1] : undefined;
}

/** Bỏ khoá rác/ trùng khỏi save trước khi xét luật mở. */
function clearedSet(cleared: string[]): Set<string> {
  if (!Array.isArray(cleared)) return new Set();
  const out = new Set<string>();
  for (const k of cleared) if (parseKey(k)) out.add(k);
  return out;
}

/**
 * QUY TẮC MỞ KHOÁ — ĐÚNG LỜI NGƯỜI DÙNG, và là bản DUY NHẤT của luật này:
 *
 * | Ô cần xét        | Mở khi                                                      |
 * |------------------|-------------------------------------------------------------|
 * | `1.1` + easy     | LUÔN mở (không thì không có cửa vào game)                    |
 * | `M` + easy       | `prevMap(M)` đã qua ở BẤT KỲ độ khó nào                      |
 * | `M` + normal     | `M` đã qua ở `easy`                                          |
 * | `M` + elite      | `M` đã qua ở `normal`                                        |
 *
 * Người chơi hiểu luật này trong 1 giây: "qua màn trước thì mở màn sau, qua Dễ thì mở Thường".
 * Màn lạ ⇒ `false` (không ném lỗi).
 */
export function isUnlocked(cleared: string[], mapId: string, diff: Difficulty): boolean {
  const map = mapById(mapId);
  if (!map) return false;
  // Độ khó lạ (save hỏng/ typo) ⇒ KHOÁ. ⚠️ Bẫy đã sập: không chặn ở đây thì `diff` lạ rơi vào
  // nhánh easy và trả `true` — người chơi vào được chế độ không tồn tại rồi vỡ ở chỗ khác.
  if (!DIFFICULTY_IDS.includes(diff)) return false;
  const done = clearedSet(cleared);

  if (diff === 'normal') return done.has(mapKey(mapId, 'easy'));
  if (diff === 'elite') return done.has(mapKey(mapId, 'normal'));

  // easy: 1.1 luôn mở, còn lại cần màn liền trước qua ở BẤT KỲ độ khó
  if (mapId === FIRST_MAP_ID) return true;
  const prev = prevMap(mapId);
  if (!prev) return false;
  return DIFFICULTY_IDS.some((d) => done.has(mapKey(prev.id, d)));
}

// ------------------------------------------------------------------ thưởng

/** Bảng thưởng (SỐ LƯỢNG TỐI ĐA) của 1 lần qua màn. */
export interface RewardSpec {
  diamonds: number;
  /** trần số mảnh tướng rơi trong 1 lần */
  heroShards: number;
  /** trần số mảnh skill rơi trong 1 lần */
  skillShards: number;
  /** tỉ lệ rơi ĐỒ HOÀN CHỈNH (0..1) — rất thấp, xem bảng bên dưới */
  fullItemChance: number;
}

/**
 * TỈ LỆ ĐỒ HOÀN CHỈNH — chỗ người dùng chỉnh dễ nhất, để RIÊNG một bảng cho dễ tìm.
 * easy 0,4% · normal 0,6% · elite 1,2% — tức trung bình ~250 lần qua màn mới có 1 món hoàn chỉnh.
 *
 * Vì sao KHÔNG nhân thẳng `rewardMult` (0,8/ 1/ 1,8): nhân thẳng ra 0,48% và 1,08% —
 * số lẻ vô nghĩa với người chơi. Chọn 0,4/ 0,6/ 1,2 để kể được câu "elite hiếm gấp đôi normal",
 * và để người dùng thấy ngay 3 con số cần sửa khi muốn "hào phóng" hơn.
 */
export const FULL_ITEM_CHANCE: Record<Difficulty, number> = { easy: 0.004, normal: 0.006, elite: 0.012 };

/** Kim cương: nền theo màn + hệ số độ khó ĐÃ GIẢM NHẸ, luôn nằm trong ngân sách 3..12. */
const DIAMOND_BASE = 3;
const DIAMOND_PER_STAGE = 0.6;
const DIAMOND_MIN = 3;
const DIAMOND_MAX = 12;
/**
 * Vì sao "giảm nhẹ" (0,6 + 0,4 × rewardMult) thay vì nhân thẳng rewardMult:
 * màn 3.5 có nền 11,4 kim cương; nhân thẳng 1,8 ⇒ 21 kim cương/ lần — gấp đôi ngân sách
 * thiết kế và làm chợ kim cương mất giá. Giảm nhẹ cho elite 12 (đúng trần), easy 10.
 */
const DIAMOND_DAMP = 0.6;

function clamp(min: number, max: number, v: number): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Số MẢNH TỐI ĐA mỗi lần rơi: nền + 1 mỗi 6 màn, elite +1. Có TRẦN CỨNG để save không phình
 * và để "mảnh" vẫn là thứ quý (mở khoá tướng/ skill bằng mảnh chứ không tặng thẳng).
 */
function shardCap(base: number, max: number, map: MapDef, diff: DifficultyDef): number {
  const grow = Math.floor(map.stageIndex / 6) + (diff.id === 'elite' ? 1 : 0);
  return clamp(base, max, base + grow);
}

/**
 * BẢNG THƯỞNG THEO MÀN + ĐỘ KHÓ.
 *
 * - Kim cương: 3..12, tăng theo màn và theo độ khó.
 * - Mảnh nhân vật 1..3 · mảnh skill 1..3 (trần, xem `rollRewards` về tỉ lệ RƠI).
 * - PHỤ KIỆN ĐÃ XOÁ HẲN (người dùng chốt) ⇒ không còn `accShards`.
 * - Màn lạ ⇒ bảng mặc định của màn 1.1 (không ném lỗi).
 */
export function rewardFor(map: MapDef, diff: Difficulty): RewardSpec {
  const safeMap = map && typeof map.stageIndex === 'number' ? map : MAPS[0];
  const d = difficultyById(diff);
  const diamonds = clamp(
    DIAMOND_MIN,
    DIAMOND_MAX,
    Math.round((DIAMOND_BASE + safeMap.stageIndex * DIAMOND_PER_STAGE) * (DIAMOND_DAMP + (1 - DIAMOND_DAMP) * d.rewardMult)),
  );
  return {
    diamonds,
    heroShards: shardCap(1, 3, safeMap, d),
    skillShards: shardCap(1, 3, safeMap, d),
    fullItemChance: FULL_ITEM_CHANCE[d.id],
  };
}

export interface RewardRoll {
  diamonds: number;
  heroShards: number;
  skillShards: number;
  /** loại ĐỒ HOÀN CHỈNH trúng được (rất hiếm) — `null` nếu không trúng */
  fullItem: 'skill' | 'hero' | null;
}

/**
 * TỈ LỆ RƠI MẢNH — "mỗi lần qua màn KHÔNG phải lúc nào cũng có mảnh".
 * Nhân vật 60% · skill 60%. Số lượng rơi nằm trong 1..trần của `RewardSpec`.
 */
const HERO_SHARD_CHANCE = 0.6;
const SKILL_SHARD_CHANCE = 0.6;

/** Trọng số loại đồ hoàn chỉnh, dùng lại chính số rng đã quay ra "trúng" (xem bên dưới). */
const FULL_ITEM_WEIGHTS: Array<{ upTo: number; kind: 'skill' | 'hero' }> = [
  { upTo: 0.6, kind: 'skill' }, // 60%
  { upTo: 1, kind: 'hero' }, // 40% — quý nhất (mở thẳng 1 nhân vật)
];

/**
 * QUAY THƯỞNG TẤT ĐỊNH — KHÔNG dùng `Math.random`.
 *
 * Vì sao bắt buộc: `tools/balance.ts` chạy hàng nghìn trận và test so số đo; dùng
 * `Math.random` là mỗi lần chạy ra một kết quả, không tái hiện được lỗi. Người gọi truyền
 * `rng` (thường là `Rng` trong `sim/rng.ts`).
 *
 * ⚠️ HỢP ĐỒNG QUAN TRỌNG: hàm này tiêu thụ ĐÚNG 3 số rng, theo thứ tự CỐ ĐỊNH
 * `[nhân vật, skill, đồ-hoàn-chỉnh]` và KHÔNG phụ thuộc kết quả.
 * (Trước đây là 4 số vì còn mảnh phụ kiện — phụ kiện đã xoá hẳn.)
 * Lý do: nếu số rng tiêu thụ phụ thuộc kết quả (kiểu "trúng thì quay thêm 1 lần nữa") thì mọi
 * thứ phía sau trong cùng chuỗi rng bị đẩy lệch ⇒ cân bằng đổi khác — đúng cái bẫy RNG đã ghi
 * trong README (hiệu ứng dùng chung rng với gameplay làm kết quả trận đấu đổi).
 *
 * Mẹo dùng CHÍNH số rng đó cho cả "có rơi không" lẫn "rơi bao nhiêu": `r` đồng đều trên
 * `[0, chance)` khi trúng, nên `r / chance` đồng đều trên `[0,1)` ⇒ số lượng vẫn ngẫu nhiên
 * đều mà KHÔNG tốn thêm số rng.
 */
export function rollRewards(spec: RewardSpec, rng: () => number): RewardRoll {
  const chance = (p: number) => Math.max(0, Math.min(1, Number.isFinite(p) ? p : 0));
  const next = () => {
    const v = typeof rng === 'function' ? rng() : 0.999;
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.999;
  };

  /** 1 số rng cho cả tỉ lệ rơi lẫn số lượng; trả 0 nếu không rơi. */
  const rollShards = (dropChance: number, cap: number): number => {
    const r = next();
    const c = chance(dropChance);
    if (cap <= 0 || c <= 0 || r >= c) return 0;
    const t = r / c; // 0..1 đồng đều
    return 1 + Math.floor(t * cap);
  };

  const diamonds = Math.max(0, Math.floor(spec?.diamonds ?? 0));
  const heroShards = rollShards(HERO_SHARD_CHANCE, Math.max(0, Math.floor(spec?.heroShards ?? 0)));
  const skillShards = rollShards(SKILL_SHARD_CHANCE, Math.max(0, Math.floor(spec?.skillShards ?? 0)));

  const fullChance = chance(spec?.fullItemChance ?? 0);
  const rFull = next();
  let fullItem: RewardRoll['fullItem'] = null;
  if (fullChance > 0 && rFull < fullChance) {
    const t = rFull / fullChance; // 0..1 đồng đều
    fullItem = (FULL_ITEM_WEIGHTS.find((w) => t < w.upTo) ?? FULL_ITEM_WEIGHTS[FULL_ITEM_WEIGHTS.length - 1]).kind;
  }

  return { diamonds, heroShards, skillShards, fullItem };
}

/**
 * MẢNH RƠI VÀO CLASS/ MÓN NÀO — cũng theo `rng`, không `Math.random`.
 *
 * ⚠️ KHUYẾN NGHỊ: truyền RNG RIÊNG cho phần chọn món (giống `World.fxRng` tách khỏi rng
 * gameplay). Hàm này chỉ tiêu thụ số rng khi THỰC SỰ cần (mảnh nào = 0 thì bỏ qua), nên nếu
 * dùng chung chuỗi với mô phỏng trận thì số lượng tiêu thụ phụ thuộc kết quả quay ⇒ dễ làm
 * lệch diễn biến trận — đúng bẫy RNG đã ghi trong README.
 *
 * Thứ tự tiêu thụ (cố định theo nhánh, ghi lại để test dùng rng giả):
 * `[nhân vật] → [class của skill] → [id skill] → [món đồ hoàn chỉnh]`.
 * (Mảnh phụ kiện đã bỏ cùng đợt xoá phụ kiện.)
 *
 * Dữ liệu rác (mảng rỗng, thiếu khoá) ⇒ trả object THIẾU khoá đó, không ném lỗi.
 */
export function pickShardTargets(
  candidates: { classes: string[]; skills: Record<string, string[]> },
  roll: RewardRoll,
  rng: () => number,
): {
  heroClass?: string;
  skillClass?: string;
  skillId?: string;
  fullItemId?: string;
} {
  const next = () => {
    const v = typeof rng === 'function' ? rng() : 0.999;
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.999;
  };
  const pick = <T>(arr: readonly T[]): T | undefined => (arr.length ? arr[Math.floor(next() * arr.length) % arr.length] : undefined);
  const asStrings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : []);

  const classes = asStrings(candidates?.classes);
  const skillTable: Record<string, string[]> = candidates?.skills && typeof candidates.skills === 'object' ? candidates.skills : {};
  const skillClasses = Object.keys(skillTable).filter((k) => asStrings(skillTable[k]).length > 0);

  /** Chọn 1 cặp (class, skill) — 2 số rng. Ưu tiên class có skill thật, không thì lấy class chung. */
  const pickSkill = (): { skillClass?: string; skillId?: string } => {
    const classPool = skillClasses.length ? skillClasses : classes;
    if (!classPool.length) return {};
    const skillClass = pick(classPool);
    if (!skillClass) return {};
    const list = asStrings(skillTable[skillClass]);
    const skillId = list.length ? pick(list) : undefined;
    return { skillClass, skillId };
  };

  const out: ReturnType<typeof pickShardTargets> = {};

  // Mảnh tướng: rơi vào 1 class (mở khoá tướng của class đó)
  if ((roll?.heroShards ?? 0) > 0 && classes.length) {
    const heroClass = pick(classes);
    if (heroClass) out.heroClass = heroClass;
  }
  // Mảnh skill: 1 class + 1 skill cụ thể trong class đó
  if ((roll?.skillShards ?? 0) > 0) {
    const s = pickSkill();
    if (s.skillClass) out.skillClass = s.skillClass;
    if (s.skillId) out.skillId = s.skillId;
  }
  // ĐỒ HOÀN CHỈNH: chọn đúng loại đã trúng
  if (roll?.fullItem === 'skill') {
    const s = pickSkill();
    if (s.skillClass) out.skillClass = out.skillClass ?? s.skillClass;
    if (s.skillId) out.skillId = out.skillId ?? s.skillId;
    out.fullItemId = s.skillId ?? s.skillClass;
  } else if (roll?.fullItem === 'hero' && classes.length) {
    const fullItemId = pick(classes);
    if (fullItemId) {
      out.fullItemId = fullItemId;
      out.heroClass = out.heroClass ?? fullItemId;
    }
  }

  return out;
}
